import { ICollapsable } from "../molecules/collapsable";
import { IFilterDependencies, TypeOfFilter } from "../../common/filter";
import { expressEventListener, expressQuerySelector, getParentWithClassName } from "../../common/html";
import { compositeDisposable } from "../../utils/disposable";
import { onlyNumber } from "../../utils/validation";
import { getParameter } from "../../utils/querystring";

export interface IRangeFilter {
	readonly filterQs: string;
	readonly disable: (selectOptionValue: string[]) => void;
	readonly enable: (selectOptionValue: string[]) => void;
	readonly getSelectOptions: () => string[];
	readonly removeRangeFilter: (update?: boolean) => void;
	readonly dispose: () => void;
}

export function createRangeFilter(
	collapsableComponent: ICollapsable,
	selectedFiltersContainerEl: HTMLElement,
	filterQs: string,
	filterCategory: string,
	filterEl: HTMLElement,
	showSliderEls: HTMLInputElement[],
	filtersContainerEl: HTMLElement,
	deps: IFilterDependencies,
): IRangeFilter {
	const containerEl = expressQuerySelector<HTMLElement>(filterEl, ".a-form-input__slider-container", true);
	const preBarEl = expressQuerySelector<HTMLElement>(containerEl, ".a-form-input__slider-pre-bar", true);
	const postBarEl = expressQuerySelector<HTMLElement>(containerEl, ".a-form-input__slider-post-bar", true);
	const rangeEl = expressQuerySelector<HTMLElement>(containerEl, ".a-form-input__slider-range", true);
	const minRangeInputEl = expressQuerySelector<HTMLInputElement>(filterEl, ".a-form-input__slider-range--min", true);
	const maxRangeInputEl = expressQuerySelector<HTMLInputElement>(filterEl, ".a-form-input__slider-range--max", true);
	const minBubbleContainerEl = expressQuerySelector<HTMLElement>(
		containerEl,
		".a-form-input__slider-bubble-container--min",
		false,
	);
	const maxBubbleContainerEl = expressQuerySelector<HTMLElement>(
		containerEl,
		".a-form-input__slider-bubble-container--max",
		false,
	);
	const minRangeThumbEl = expressQuerySelector<HTMLElement>(filterEl, ".a-form-input__slider-thumb--min", true);
	const maxRangeThumbEl = expressQuerySelector<HTMLElement>(filterEl, ".a-form-input__slider-thumb--max", true);
	let minBubbleInputEl: HTMLInputElement, maxBubbleInputEl: HTMLInputElement;
	if (minBubbleContainerEl)
		minBubbleInputEl = expressQuerySelector<HTMLInputElement>(
			minBubbleContainerEl,
			".a-form-input__slider-bubble--min",
			true,
		);
	if (maxBubbleContainerEl)
		maxBubbleInputEl = expressQuerySelector<HTMLInputElement>(
			maxBubbleContainerEl,
			".a-form-input__slider-bubble--max",
			true,
		);
	const disposableEvents = compositeDisposable([]);
	const selectedFilter: string = getParameter(filterQs);
	const originalMinValue: number = parseInt(minRangeInputEl.value);
	const originalMaxValue: number = parseInt(maxRangeInputEl.value);
	let currentMinValue: number = parseInt(minRangeInputEl.value);
	let currentMaxValue: number = parseInt(maxRangeInputEl.value);
	let previousMinValue: number | undefined;
	let previousMaxValue: number | undefined;

	const {
		updateParametersForUrl,
		removeFromFilterContainer,
		onFilter,
		attachRemoveFilterEvent,
		toggleResetFilters,
		addTextToFilterContainer,
	} = deps;

	const percent = (value: number, min: number, max: number) => Math.ceil(((value - min) / (max - min)) * 100);

	const moveToTop = (input: HTMLInputElement) => {
		if (input === maxRangeInputEl) {
			maxRangeInputEl.style.zIndex = "2";
			minRangeInputEl.style.zIndex = "1";
		} else {
			maxRangeInputEl.style.zIndex = "1";
			minRangeInputEl.style.zIndex = "2";
		}
	};

	const setValues = (minValue: number, maxValue: number) => {
		setMinValue(minValue);
		setMaxValue(maxValue);
	};

	const setMinValue = (minValue: number) => {
		const updateMinBar = (value: number) => {
			const percentStyle = percent(value, parseInt(minRangeInputEl.min), parseInt(minRangeInputEl.max)) + "%";
			preBarEl.style.width = percentStyle;
			rangeEl.style.left = percentStyle;
			minRangeThumbEl.style.left = percentStyle;
		};

		minRangeInputEl.value = minValue + "";
		if (minBubbleInputEl) minBubbleInputEl.value = minValue + "";
		updateMinBar(minValue);
		currentMinValue = minValue;
	};

	const setMaxValue = (maxValue: number) => {
		const updateMaxBar = (value: number) => {
			const percentage = percent(value, parseInt(maxRangeInputEl.min), parseInt(maxRangeInputEl.max));
			const percentStyle = percentage + "%";
			const reversePercentStyle = 100 - percentage + "%";
			postBarEl.style.width = reversePercentStyle;
			rangeEl.style.right = reversePercentStyle;
			maxRangeThumbEl.style.left = percentStyle;
		};

		maxRangeInputEl.value = maxValue + "";
		if (maxBubbleInputEl) maxBubbleInputEl.value = maxValue + "";
		updateMaxBar(maxValue);
		currentMaxValue = maxValue;
	};

	const performFiltering = (updateQuerystring = true) => {
		// Nothing changed => exit
		if (previousMinValue === currentMinValue && previousMaxValue === currentMaxValue) return;
		previousMinValue = currentMinValue;
		previousMaxValue = currentMaxValue;
		// Perform the actual filtering
		const value = isNoneInputSelected()
			? "0"
			: currentMinValue == currentMaxValue
				? currentMaxValue.toString()
				: `${currentMinValue} - ${currentMaxValue}`;
		addTextToFilterContainer &&
			addTextToFilterContainer(
				filtersContainerEl,
				TypeOfFilter.Slider,
				selectedFiltersContainerEl,
				filterCategory,
				value,
				removeRangeFilter,
			);
		triggerFilter(true, getSliderValue());
		if (updateQuerystring)
			updateParametersForUrl &&
				updateParametersForUrl(TypeOfFilter.Slider, filterQs, getSliderValue(), false, filterEl.dataset.analytics);
	};

	const isNoneInputSelected = () => {
		return showSliderEls.some((x) => !x.classList.contains("technical-show-slider") && x.checked);
	};

	const getSliderValue = () => {
		const isNoneSelected = isNoneInputSelected();
		const checkedOptionValue =
			showSliderEls.length > 0 && showSliderEls.some((x) => x.checked)
				? `${showSliderEls.find((x) => x.checked).value}`
				: "";
		return isNoneSelected
			? `${checkedOptionValue}_0`
			: `${checkedOptionValue}_${minBubbleInputEl.value}-${maxBubbleInputEl.value}`;
	};

	const triggerFilter = (update: boolean, valueOverride?: string) => {
		onFilter(false, filterQs, valueOverride || getSliderValue(), true, containerEl, update);
	};

	const toggleOption = (selectOptionValue: string[], enable: boolean) => {
		if (!showSliderEls || !showSliderEls.length) return;
		const originalSliderValue = getSliderValue();
		const isFilterActive = showSliderEls.some((x) => x.checked);

		showSliderEls.forEach((x) => {
			if (!selectOptionValue.includes(x.value)) return;

			const filterValue = getParentWithClassName(x, "a-form-input");
			filterValue?.classList.toggle("a-form-input--disabled", !enable);
			x.toggleAttribute("disabled", !enable);
			x.disabled = !enable;
		});

		// if only 1 option is left check it by default
		if (showSliderEls.filter((x) => !x.disabled).length > 1 || !isFilterActive) return;
		showSliderEls.forEach((x) => (x.checked = !x.disabled));
		filterEl.classList.remove("u-hide");

		const afterChangesSliderValue = getSliderValue();
		if (originalSliderValue == afterChangesSliderValue) return;
		triggerFilter(true);
		updateParametersForUrl &&
			updateParametersForUrl(TypeOfFilter.Slider, filterQs, getSliderValue(), false, filterEl.dataset.analytics);
	};

	const removeRangeFilter = (update = true) => {
		const deselect = (currentEl?: HTMLInputElement | null) => {
			if (showSliderEls.length) filterEl.classList.add("u-hide");

			showSliderEls.forEach((chk) => {
				if (currentEl && currentEl.id !== chk.id) chk.checked = false;
				if (!currentEl) chk.checked = false;
			});
		};

		deselect();

		setValues(originalMinValue, originalMaxValue);
		const value = getSliderValue();
		onFilter && onFilter(false, filterQs, value, false, containerEl, update);
		updateParametersForUrl &&
			updateParametersForUrl(TypeOfFilter.Slider, filterQs, value, true, filterEl.dataset.analytics);
		removeFromFilterContainer &&
			removeFromFilterContainer(filtersContainerEl, selectedFiltersContainerEl, filterCategory);
	};

	const addHandlers = () => {
		// This event is fired when the user changes the bubble input. It will update the position of the slider handles
		const onBubbleInputElConfirm = (ev: Event) => {
			if (!minBubbleInputEl.value) minBubbleInputEl.value = currentMinValue.toString();
			if (!maxBubbleInputEl.value) maxBubbleInputEl.value = currentMaxValue.toString();

			// First update the values, then performFiltering
			updateSliderValues(ev);
			performFiltering();
		};

		// This event is triggered when the user inputs in one of the bubble inputs or moves one of the handles
		const updateSliderValues = (ev: Event) => {
			const target = ev.target as HTMLInputElement;
			if (!onlyNumber(target.value) || isNaN(parseInt(target.value))) {
				if (target.value === "") return;
				if (target === minBubbleInputEl || target === minRangeInputEl) {
					target.value = currentMinValue.toString();
				} else if (target === maxBubbleInputEl || target === maxRangeInputEl) {
					target.value = currentMaxValue.toString();
				}
			} else {
				// Move the handles
				let value: number;

				const fixWrongValue = (value: number, correctionValue: number) => {
					if (value !== 0 && !value) {
						value = correctionValue;
					}
					return value;
				};

				const targetValue = parseInt(target.value);
				const minRangeInputElValue = parseInt(minRangeInputEl.value);
				const minRangeInputElMinValue = parseInt(minRangeInputEl.min);
				const maxRangeInputElValue = parseInt(maxRangeInputEl.value);
				const maxRangeInputElMaxValue = parseInt(maxRangeInputEl.max);
				if (target === minBubbleInputEl || target === minRangeInputEl) {
					value = targetValue < minRangeInputElMinValue ? minRangeInputElMinValue : targetValue;
					value = fixWrongValue(value, minRangeInputElValue);
					value = value > maxRangeInputElValue ? maxRangeInputElValue : value;
					setMinValue(value);
					moveToTop(minRangeInputEl);
				} else if (target === maxBubbleInputEl || target === maxRangeInputEl) {
					value = targetValue > maxRangeInputElMaxValue ? maxRangeInputElMaxValue : targetValue;
					value = fixWrongValue(value, maxRangeInputElValue);
					value = value < minRangeInputElValue ? minRangeInputElValue : value;
					setMaxValue(value);
					moveToTop(maxRangeInputEl);
				}
			}
		};

		if (minBubbleInputEl) {
			disposableEvents.add(expressEventListener(minBubbleInputEl, "change", onBubbleInputElConfirm));
			disposableEvents.add(expressEventListener(minBubbleInputEl, "focusout", onBubbleInputElConfirm));
		}

		if (maxBubbleInputEl) {
			disposableEvents.add(expressEventListener(maxBubbleInputEl, "change", onBubbleInputElConfirm));
			disposableEvents.add(expressEventListener(maxBubbleInputEl, "focusout", onBubbleInputElConfirm));
		}

		disposableEvents.add(expressEventListener(minRangeInputEl, "input", updateSliderValues));
		disposableEvents.add(expressEventListener(minRangeInputEl, "change", (e: Event) => performFiltering()));
		disposableEvents.add(
			expressEventListener(minRangeInputEl, "mousedown", () => {
				moveToTop(minRangeInputEl);
			}),
		);

		disposableEvents.add(expressEventListener(maxRangeInputEl, "input", updateSliderValues));
		disposableEvents.add(
			expressEventListener(maxRangeInputEl, "mousedown", () => {
				moveToTop(maxRangeInputEl);
			}),
		);
		disposableEvents.add(expressEventListener(maxRangeInputEl, "change", (e: Event) => performFiltering()));
	};

	const toggleSlider = (el: HTMLInputElement) => {
		if (!showSliderEls.length) return;

		if (showSliderEls.every((f) => !f.checked)) removeRangeFilter();
		else {
			const showSlider = el.classList.contains("technical-show-slider") && el.checked;

			if (showSlider) {
				const max = maxRangeInputEl.max;
				const min = minRangeInputEl.min;
				const sameMinMaxValue = min === max;
				setValues(parseInt(min), parseInt(max));
				addTextToFilterContainer &&
					addTextToFilterContainer(
						filtersContainerEl,
						TypeOfFilter.Slider,
						selectedFiltersContainerEl,
						filterCategory,
						sameMinMaxValue ? min : min + " - " + max,
						removeRangeFilter,
					);
				filterEl.classList.toggle("u-hide", sameMinMaxValue);
				triggerFilter(true);
			} else {
				addTextToFilterContainer &&
					addTextToFilterContainer(
						filtersContainerEl,
						TypeOfFilter.Slider,
						selectedFiltersContainerEl,
						filterCategory,
						"0",
						removeRangeFilter,
					);
				filterEl.classList.add("u-hide");
				triggerFilter(true);
			}
		}
	};

	if (selectedFiltersContainerEl && attachRemoveFilterEvent)
		attachRemoveFilterEvent(selectedFiltersContainerEl, filterCategory, removeRangeFilter);

	showSliderEls.forEach((el) =>
		expressEventListener(el, "click", () => {
			toggleSlider(el);
			updateParametersForUrl &&
				updateParametersForUrl(
					TypeOfFilter.Slider,
					filterQs,
					getSliderValue(),
					el.classList.contains("technical-show-slider") && !el.checked,
					filterEl.dataset.analytics,
				);
		}),
	);

	if (!showSliderEls.length) {
		filterEl.classList.remove("u-hide");
	}

	if (selectedFilter && collapsableComponent) {
		const selectedOption = selectedFilter.split("_");
		const values = (selectedOption[1] || selectedFilter).split("-");
		// We need to validate the querystring value and perform some corrections
		let min: number;
		let max: number;
		let querystringCorrected = false;
		if (values[0] && onlyNumber(values[0]) && !isNaN(parseInt(values[0]))) {
			min = parseInt(values[0]);
		} else {
			min = parseInt(minRangeInputEl.min);
			querystringCorrected = true;
		}
		if (values[1] && onlyNumber(values[1]) && !isNaN(parseInt(values[0]))) {
			max = parseInt(values[1]);
		} else {
			max = parseInt(maxRangeInputEl.max);
			querystringCorrected = true;
		}
		if (min > max) {
			min = max;
			querystringCorrected = true;
		}
		if (max < min) {
			max = min;
			querystringCorrected = true;
		}

		if (selectedOption[0] != "none" && showSliderEls) {
			showSliderEls.forEach((x) => (x.checked = selectedOption[0].toLowerCase() == x.value.toLowerCase()));
			filterEl.classList.toggle("u-hide", min === max);
		}

		setValues(min, max);

		const minValueMinslider = parseInt(minRangeInputEl.min);
		const maxValueMaxSlider = parseInt(maxRangeInputEl.max);

		if (min == max && (max == minValueMinslider || min == maxValueMaxSlider)) {
			const sliderCompletelyOnMax = min == maxValueMaxSlider;
			if (sliderCompletelyOnMax) moveToTop(minRangeInputEl);
			else moveToTop(maxRangeInputEl);
		}
		performFiltering(querystringCorrected);
		toggleResetFilters && toggleResetFilters(filtersContainerEl, false);
		collapsableComponent.open();
	}

	// Correct the bubble values if they are visible and haven't got a value
	if ((minBubbleInputEl && !minBubbleInputEl.value) || (maxBubbleInputEl && !maxBubbleInputEl.value)) {
		if (minBubbleInputEl && !minBubbleInputEl.value) minBubbleInputEl.value = currentMinValue.toString();
		if (maxBubbleInputEl && !maxBubbleInputEl.value) maxBubbleInputEl.value = currentMaxValue.toString();
	}

	addHandlers();

	return {
		filterQs,
		removeRangeFilter,
		disable: (selectOptionValue: string[]) => toggleOption(selectOptionValue, false),
		enable: (selectOptionValue: string[]) => toggleOption(selectOptionValue, true),
		getSelectOptions: () => showSliderEls.map((x) => x.value),
		dispose: () => disposableEvents.dispose(),
	} as IRangeFilter;
}
